import { useEffect, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  useOrganizationStore,
  useOrganization,
  useOrganizationLoading,
  useOrganizationError,
} from "../store/organizationStore";

interface OrganizationLoaderProps {
  children: ReactNode;
}

/**
 * Component that loads organization info before rendering children.
 * This is independent of authentication and should wrap all portal routes.
 * The organization info is loaded via the public /organization-info endpoint
 * based on the current URL's subdomain or custom portal URL.
 */
export const OrganizationLoader = ({ children }: OrganizationLoaderProps) => {
  const { t } = useTranslation();
  const fetchOrganization = useOrganizationStore((state) => state.fetchOrganization);
  const organization = useOrganization();
  const isLoading = useOrganizationLoading();
  const error = useOrganizationError();

  // Fetch organization on mount
  useEffect(() => {
    fetchOrganization();
  }, [fetchOrganization]);

  // Show loading state while fetching
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  // Show error state if organization fetch failed
  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4">
        <div className="text-destructive font-medium">{t("Unable to load portal")}</div>
        <div className="text-muted-foreground text-sm">{error}</div>
      </div>
    );
  }

  // Wait for organization to be loaded before rendering children
  if (!organization) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return <>{children}</>;
};
